/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { isElectronApp } from '@shared/utils/env';

/**
 * Base utilities for SkillShop service
 * 
 * Architecture:
 * - SkillShop server calls (port 5173): Use authenticatedFetch() - handles auth, routing
 * - Product server calls (port 3002): Use direct fetch() - local endpoints with repo context
 * 
 * In Electron production mode:
 * - Frontend is served from Velocity server (port 3002)
 * - SkillShop server runs on port 5173
 * - We must use absolute URLs to reach the correct server
 */

/**
 * Get the base URL for product server endpoints (port 3002)
 * These endpoints handle repo-specific operations
 */
export function getProductServerBaseUrl(): string {
  // In development, always use explicit port
  if (import.meta.env.DEV) {
    return 'http://localhost:3002';
  }
  
  // In Electron production, the frontend is served from port 3002
  // Relative URLs will go to the same origin, which is correct
  if (isElectronApp()) {
    return 'http://localhost:3002';
  }
  
  // In web production, use relative URL (same origin)
  return '';
}

/**
 * Get the base URL for SkillShop server endpoints (port 5173)
 * These endpoints handle authenticated operations
 */
export function getSkillShopServerBaseUrl(): string {
  // In development, always use explicit port
  if (import.meta.env.DEV) {
    return 'http://localhost:5173';
  }
  
  // In Electron production, SkillShop server runs on port 5173
  // We need absolute URL since frontend is on port 3002
  if (isElectronApp()) {
    return 'http://localhost:5173';
  }
  
  // In web production, use relative URL (same origin)
  return '';
}

